import { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { getStudentSubjectsById, getStudentWorkSummaryHistory, getStudentModulesBySubject, getStudentWorkWeeksList } from '@app/services/student';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StudentDetailsCard from '../components/StudentDetailsCard';

const WorkHistory = () => {

    const canAccess = useSelector((state: any) => state.auth.permissions['student-work']);
    const navigate = useNavigate();
    const [t] = useTranslation();

    useEffect(() => {
        if (canAccess.history == false) {
            toast.error(t('auth.noAccess'));
            navigate('/');
        }
    }, []);

    const { id } = useSelector((state: any) => state?.auth?.selectedStudent);
    const studentWorkStore = useSelector((state: any) => state.studentWork);

    const [isLoading, setLoading] = useState(false);
    const [weeklyWork, setWeeklyWork] = useState([]);
    const [studentWork, setStudentWork] = useState([]);
    const [activeWeek, setActiveWeek] = useState(1);
    const [totalWeek, setTotalWeek] = useState(0);
    const [filteredWeeks, setFilteredWeeks] = useState<number[]>([]);
    const [streamPerWeek, setStreamPerWeek] = useState(0);
    const [weeks, setWeeks] = useState([]);
    const [allWeeks, setAllWeeks] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [modules, setModules] = useState([]);
    const [stream1, setStream1] = useState('');
    const [stream2, setStream2] = useState('');
    const [stream3, setStream3] = useState('');
    const [stream4, setStream4] = useState('');
    const [selectedModulePathway, setSelectedModulePathway] = useState('');
    const [search, setSearch] = useState({
        subject_id: studentWorkStore?.subject?.id ?? 0,
        module_id: '',
        week_number: ''
    });

    useEffect(() => {
        getStudentAllActiveSubjects(id);
        if (search.subject_id !== undefined && search.subject_id !== null && search.subject_id !== '') {
            fetchModulesBySubject(id, search.subject_id);
        }
        fetchWeeksList(id, search.subject_id);
        getWorkSummaryHistoryData(id, search.subject_id, search.week_number, search.module_id);
    }, []);

    const getStudentAllActiveSubjects = async (studentId: any) => {
        try {
            const resp = await getStudentSubjectsById(studentId);
            if (resp.success === true) {
                setSubjects([{ id: 0, name: 'Mix and Match' }].concat(resp?.collections));
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getWorkSummaryHistoryData = async (student_id: any, subject_id: any, week = '', module_id = '') => {
        try {
            setLoading(true);
            const payload = {
                student_id: parseInt(student_id),
                subject_id: parseInt(subject_id),
                week,
                module_id
            };
            const resp = await getStudentWorkSummaryHistory(payload);
            if (resp.success === true) {
                setLoading(false);
                const data = resp?.collections;
                setStudentWork(data.summary);
                setStreamPerWeek(data.modules_per_week);
                setTotalWeek(data.total_week || data.global_week_counter);

                if (!week && data.current_week) {
                    setSearch(prev => ({ ...prev, week_number: data.current_week.toString() }));
                    setActiveWeek(data.current_week);
                }

                if (data.summary.length > 0) {
                    setWeeklyWork(data.summary[0]);
                    if (data.modules_per_week >= 1) setStream1(data.module1 ?? '');
                    if (data.modules_per_week >= 2) setStream2(data.module2 ?? '');
                    if (data.modules_per_week >= 3) setStream3(data.module3 ?? '');
                    if (data.modules_per_week >= 4) setStream4(data.module4 ?? '');

                    if (module_id) {
                        const firstDay = data.summary[0]?.find((day: any) => day.modules && day.modules.length > 0);
                        if (firstDay) {
                            const moduleData = firstDay.modules.find((m: any) => m.module_id == module_id);
                            if (moduleData) setSelectedModulePathway(data[moduleData.module] || '');
                        }
                    } else {
                        setSelectedModulePathway('');
                    }
                } else {
                    setWeeklyWork([]);
                    setSelectedModulePathway('');
                }
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const fetchModulesBySubject = async (student_id: any, subject_id: any) => {
        try {
            const resp = await getStudentModulesBySubject({ student_id: parseInt(student_id), subject_id: parseInt(subject_id) });
            if (resp.success === true) {
                setModules((resp?.collections || []).map((m: any) => ({
                    id: m.module_id.toString(),
                    name: `${m.module_name} [Worksheets: ${m.worksheet_from}-${m.worksheet_to}]`,
                    available_weeks: m.available_weeks || []
                })));
            }
        } catch (error: any) {
            console.error('Error fetching modules:', error);
        }
    };

    const fetchWeeksList = async (student_id: any, subject_id: any) => {
        try {
            const resp = await getStudentWorkWeeksList({ student_id: parseInt(student_id), subject_id: parseInt(subject_id) });
            if (resp?.success === true) {
                const weeksArr = (resp.collections || []).map((w: any) => ({
                    id: w.week_number,
                    name: `${w.week_label} - ${moment(w.start_date).format('DD/MM/YYYY')}`
                }));
                setAllWeeks(weeksArr);
                setWeeks(weeksArr);
            }
        } catch (error: any) {
            console.error('Error fetching weeks list:', error);
        }
    };

    const handleSubjectChange = async (subject_id: any) => {
        setSearch({ ...search, subject_id, module_id: '', week_number: '' });
        setFilteredWeeks([]);
        await fetchModulesBySubject(id, subject_id);
        fetchWeeksList(id, subject_id);
        getWorkSummaryHistoryData(id, subject_id, '', '');
    };

    const handleModuleChange = (selectedOption: any) => {
        const module_id = selectedOption ? selectedOption.value : '';

        if (selectedOption && selectedOption.available_weeks?.length > 0) {
            const availableWeeks: number[] = selectedOption.available_weeks;
            setFilteredWeeks(availableWeeks);
            const filtered = allWeeks.filter((w: any) => availableWeeks.includes(w.id));
            setWeeks(filtered);

            const currentWeek = search.week_number ? parseInt(search.week_number) : activeWeek;
            const targetWeek = availableWeeks.includes(currentWeek)
                ? currentWeek.toString()
                : availableWeeks[0].toString();

            setSearch({ ...search, module_id, week_number: targetWeek });
            getWorkSummaryHistoryData(id, search.subject_id, targetWeek, module_id);
            return;
        } else if (!selectedOption) {
            setFilteredWeeks([]);
            setWeeks(allWeeks);
        }

        setSearch({ ...search, module_id });
        getWorkSummaryHistoryData(id, search.subject_id, search.week_number, module_id);
    };

    const handleWeekChange = (selectedOption: any) => {
        const week_number = selectedOption ? selectedOption.value : '';
        setSearch({ ...search, week_number });
        getWorkSummaryHistoryData(id, search.subject_id, week_number, search.module_id);
    };

    const changeWeek = (e: any, week: any) => {
        e.preventDefault();
        setActiveWeek(week);
        setSearch({ ...search, week_number: week.toString() });
        getWorkSummaryHistoryData(id, search.subject_id, week.toString(), search.module_id);
    };

    const getPaginationContent = () => {
        const content = [];
        const currentWeek = search.week_number ? parseInt(search.week_number) : activeWeek;
        const weekNumbers = filteredWeeks.length > 0
            ? filteredWeeks
            : Array.from({ length: totalWeek }, (_, i) => i + 1);

        if (weekNumbers.length === 0) return content;

        const currentIndex = weekNumbers.indexOf(currentWeek);
        const maxVisible = 5;
        let startIdx = Math.max(0, currentIndex - Math.floor(maxVisible / 2));
        let endIdx = Math.min(weekNumbers.length - 1, startIdx + maxVisible - 1);
        if (endIdx - startIdx < maxVisible - 1) startIdx = Math.max(0, endIdx - maxVisible + 1);

        if (currentIndex > 0) content.push(<li className="page-item" key="prev"><a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[currentIndex - 1])}>«</a></li>);
        if (startIdx > 0) {
            content.push(<li className="page-item" key={weekNumbers[0]}><a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[0])}>Week {weekNumbers[0]}</a></li>);
            if (startIdx > 1) content.push(<li className="page-item disabled" key="dots1"><span className="page-link">...</span></li>);
        }
        for (let i = startIdx; i <= endIdx; i++) {
            const w = weekNumbers[i];
            content.push(<li className="page-item" key={w}><a className="page-link" style={{ backgroundColor: currentWeek == w ? '#dee2e6' : '#fff' }} href="#" onClick={(e) => changeWeek(e, w)}>Week {w}</a></li>);
        }
        if (endIdx < weekNumbers.length - 1) {
            if (endIdx < weekNumbers.length - 2) content.push(<li className="page-item disabled" key="dots2"><span className="page-link">...</span></li>);
            content.push(<li className="page-item" key={weekNumbers[weekNumbers.length - 1]}><a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[weekNumbers.length - 1])}>Week {weekNumbers[weekNumbers.length - 1]}</a></li>);
        }
        if (currentIndex < weekNumbers.length - 1) content.push(<li className="page-item" key="next"><a className="page-link" href="#" onClick={(e) => changeWeek(e, weekNumbers[currentIndex + 1])}>»</a></li>);
        return content;
    };

    return (
        <div>
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Work History</span>
                        </div>
                        <div className="col-md-12">
                            <StudentDetailsCard />
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Work History {studentWork.length == 0 ? '' : `- Week ${search.week_number || activeWeek}`}</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-4">
                                            <label>Subject</label>
                                            <select
                                                className="form-control"
                                                name="subject_id"
                                                onChange={(e) => handleSubjectChange(e.target.value)}
                                                value={search.subject_id}
                                            >
                                                {subjects.map((obj: any) => (
                                                    <option key={obj.id} value={obj.id}>{obj.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-sm-4">
                                            <label>Module</label>
                                            <Select
                                                name="module_id"
                                                placeholder="All Modules"
                                                options={modules.map((m: any) => ({ value: m.id, label: m.name, available_weeks: m.available_weeks }))}
                                                onChange={handleModuleChange}
                                                value={search.module_id ? modules.map((m: any) => ({ value: m.id, label: m.name, available_weeks: m.available_weeks })).find((opt: any) => opt.value === search.module_id) : null}
                                                isLoading={isLoading}
                                                isClearable={true}
                                            />
                                        </div>
                                        <div className="col-sm-4">
                                            <label>Week</label>
                                            <Select
                                                name="week_number"
                                                placeholder="Select week"
                                                options={weeks.map((w: any) => ({ value: w.id.toString(), label: w.name }))}
                                                onChange={handleWeekChange}
                                                value={search.week_number ? weeks.map((w: any) => ({ value: w.id.toString(), label: w.name })).find((opt: any) => opt.value === search.week_number) : null}
                                                isLoading={isLoading}
                                                isClearable={false}
                                            />
                                        </div>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="table table-bordered">
                                            {weeklyWork.length > 0 && (
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 10 }}>#</th>
                                                        <th>Date</th>
                                                        {streamPerWeek >= 1 && !search.module_id && <th>{stream1}</th>}
                                                        {streamPerWeek >= 2 && !search.module_id && <th>{stream2}</th>}
                                                        {streamPerWeek >= 3 && !search.module_id && <th>{stream3}</th>}
                                                        {streamPerWeek >= 4 && !search.module_id && <th>{stream4}</th>}
                                                        {search.module_id && <th>{selectedModulePathway}</th>}
                                                    </tr>
                                                </thead>
                                            )}
                                            <tbody>
                                                {weeklyWork.length > 0 && weeklyWork.map((obj: any, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}.</td>
                                                        <td>{moment(obj.date).format('DD/MM/YYYY, dddd')}</td>
                                                        {!search.module_id && streamPerWeek >= 1 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module1'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {!search.module_id && streamPerWeek >= 2 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module2'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {!search.module_id && streamPerWeek >= 3 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module3'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {!search.module_id && streamPerWeek >= 4 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module4'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {search.module_id && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module_id == search.module_id), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                    </tr>
                                                ))}
                                                {weeklyWork.length == 0 && (
                                                    <tr>
                                                        <td className="text-center" colSpan={6}>There are no work history to display</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="card-footer clearfix">
                                    {!isLoading && (
                                        <ul className="pagination pagination-sm m-0 float-right">
                                            {getPaginationContent()}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WorkHistory;
